import { useEffect, useRef } from "react";

/**
 * Triggers a `window.location.reload()` once per day at the configured HH:MM.
 *
 * - `time` format: "HH:MM" (24h). Empty/invalid disables the hook.
 * - Uses setTimeout-based scheduling so it survives long-running tabs without
 *   accumulating drift from setInterval.
 * - Guards against double-fire within the same minute via a sessionStorage flag.
 */
const STORAGE_KEY = "fila_inteligente.last_auto_reload";

const parseTime = (str) => {
  if (typeof str !== "string") return null;
  const m = str.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return { h, min };
};

const msUntil = ({ h, min }) => {
  const now = new Date();
  const target = new Date();
  target.setHours(h, min, 0, 0);
  if (target.getTime() <= now.getTime()) {
    // already passed today → schedule tomorrow
    target.setDate(target.getDate() + 1);
  }
  return target.getTime() - now.getTime();
};

export const useScheduledReload = (time) => {
  const timerRef = useRef(null);

  useEffect(() => {
    const parsed = parseTime(time);
    if (!parsed) return undefined;

    const schedule = () => {
      const delay = msUntil(parsed);
      timerRef.current = window.setTimeout(() => {
        // Guard against same-minute re-fire (in case of clock skew or remount)
        const stamp = new Date().toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
        const last = (() => {
          try {
            return sessionStorage.getItem(STORAGE_KEY);
          } catch {
            return null;
          }
        })();
        if (last !== stamp) {
          try {
            sessionStorage.setItem(STORAGE_KEY, stamp);
          } catch {
            /* ignore */
          }
          window.location.reload();
          return;
        }
        // Already reloaded this minute somehow → just reschedule
        schedule();
      }, delay);
    };

    schedule();
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [time]);
};
